import {
  KOFI_BUTTON_COLOR,
  KOFI_BUTTON_CONTAINER_MARKER,
  KOFI_PAGE_ID,
  KOFI_WIDGET_SCRIPT_URL,
} from "@site/config/kofiConfig";
import { getSiteStrings, type LocaleId } from "@dawn/i18n";

interface KofiWidget {
  init(text: string, color: string, id: string): void;
  getHTML(): string;
}

declare global {
  interface Window {
    kofiwidget2?: KofiWidget;
  }
}

export function shouldShowGameKofi(): boolean {
  return import.meta.env.PROD || import.meta.env.VITE_SHOW_KOFI === "true";
}

function splitKofiWidgetHtml(html: string): { assets: string; button: string } {
  const markerIndex = html.indexOf(KOFI_BUTTON_CONTAINER_MARKER);
  if (markerIndex === -1) {
    throw new Error("Ko-fi widget markup is missing the button container.");
  }

  return {
    assets: html.slice(0, markerIndex),
    button: html.slice(markerIndex),
  };
}

function loadKofiWidgetScript(): Promise<void> {
  if (window.kofiwidget2) {
    return Promise.resolve();
  }

  const existingScript = document.querySelector(`script[src="${KOFI_WIDGET_SCRIPT_URL}"]`);
  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Failed to load Ko-fi widget.")),
        { once: true },
      );
      if (window.kofiwidget2) {
        resolve();
      }
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = KOFI_WIDGET_SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Ko-fi widget."));
    document.head.appendChild(script);
  });
}

function injectKofiAssets(assets: string): void {
  if (!assets || document.getElementById("kofi-widget-assets")) {
    return;
  }

  const holder = document.createElement("div");
  holder.id = "kofi-widget-assets";
  holder.hidden = true;
  holder.innerHTML = assets;
  document.head.append(...Array.from(holder.querySelectorAll("style, link")));
}

let cachedKofiButton: { locale: LocaleId; html: string } | null = null;
const kofiButtonBuilds = new Map<LocaleId, Promise<string>>();

async function buildKofiButtonHtmlUncached(locale: LocaleId): Promise<string> {
  await loadKofiWidgetScript();

  const widget = window.kofiwidget2;
  if (!widget) {
    throw new Error("Ko-fi widget is unavailable after script load.");
  }

  const buttonText = getSiteStrings(locale).kofiButtonLabel;
  widget.init(buttonText, KOFI_BUTTON_COLOR, KOFI_PAGE_ID);
  const { assets, button } = splitKofiWidgetHtml(widget.getHTML());
  injectKofiAssets(assets);
  return button;
}

/** Build localized Ko-fi button HTML (loads script + injects assets once). */
export async function buildKofiButtonHtml(locale: LocaleId): Promise<string> {
  if (cachedKofiButton?.locale === locale) {
    return cachedKofiButton.html;
  }

  let pending = kofiButtonBuilds.get(locale);
  if (!pending) {
    pending = buildKofiButtonHtmlUncached(locale)
      .then((html) => {
        cachedKofiButton = { locale, html };
        kofiButtonBuilds.delete(locale);
        return html;
      })
      .catch((error) => {
        kofiButtonBuilds.delete(locale);
        throw error;
      });
    kofiButtonBuilds.set(locale, pending);
  }

  return pending;
}
